import { motion } from 'framer-motion'
import { Car, Truck, Zap, Droplets, Hammer, Paintbrush } from 'lucide-react'

const services = [
  {
    id: 'freteiro',
    title: 'Freteiro',
    img: '/images/freteiro.webp',
    icon: Truck,
    description:
      'Fretes e pequenas mudanças com agilidade, cuidado e atendimento confiável.',
  },
  {
    id: 'eletricos',
    title: 'Reparos e Instalação Elétricos',
    img: '/images/eletricos.webp',
    icon: Zap,
    description:
      'Tomadas, interruptores, luminárias e pequenos reparos elétricos com segurança.',
  },
  {
    id: 'hidraulicos',
    title: 'Reparos e Instalação Hidráulicos',
    img: '/images/hidraulicos.webp',
    icon: Droplets,
    description:
      'Torneiras, sifões, vazamentos, trocas e ajustes hidráulicos para residências e empresas.',
  },
  {
    id: 'moveis',
    title: 'Montagem de Móveis',
    img: '/images/moveis.webp',
    icon: Hammer,
    description:
      'Montagem e desmontagem de móveis com precisão, organização e acabamento profissional.',
  },
  {
    id: 'pinturas',
    title: 'Pinturas em Geral',
    img: '/images/pinturas.webp',
    icon: Paintbrush,
    description:
      'Pintura de paredes, retoques e renovação de ambientes com ótimo acabamento.',
  },
  {
    id: 'motorista',
    title: 'Motorista Particular',
    img: '/images/motorista.webp',
    icon: Car,
    description:
      'Transporte com discrição, segurança e pontualidade para compromissos, viagens e deslocamentos.',
  },
]

const ServiceCard = ({ service, index }) => {
  const Icon = service.icon
  return (
    <motion.div
      data-testid={`service-card-${service.id}`}
      className="group relative overflow-hidden bg-white border border-[#062B63]/5 cursor-default"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.6, delay: index * 0.08, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      {/* Image container — 4:3 ratio, no crop */}
      <div className="relative aspect-[4/3] overflow-hidden">
        <motion.img
          src={service.img}
          alt={service.title}
          className="w-full h-full object-cover"
          whileHover={{ scale: 1.07 }}
          transition={{ duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94] }}
        />
        {/* Dark overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#062B63]/55 via-[#062B63]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-350" />

        {/* Icon badge */}
        <div className="absolute bottom-0 left-0 w-12 h-12 bg-[#062B63] flex items-center justify-center group-hover:bg-[#F2B300] transition-colors duration-300">
          <Icon
            size={20}
            className="text-[#F2B300] group-hover:text-[#062B63] transition-colors duration-300"
          />
        </div>
      </div>

      {/* Content */}
      <div className="p-6 pb-7">
        <h3 className="text-[#062B63] font-bold text-[1.05rem] mb-2.5 font-outfit leading-snug">
          {service.title}
        </h3>
        <p className="text-[#062B63]/58 text-sm leading-relaxed">{service.description}</p>
      </div>

      {/* Bottom gold accent */}
      <div className="absolute bottom-0 left-0 h-[2px] w-0 bg-[#F2B300] group-hover:w-full transition-all duration-500" />
    </motion.div>
  )
}

const ServicesSection = () => {
  return (
    <section
      id="servicos"
      data-testid="services-section"
      className="py-24 md:py-32 bg-[#F7F7F7]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section header */}
        <div className="mb-14 lg:mb-16">
          <motion.p
            className="text-[#F2B300] text-[11px] font-bold uppercase tracking-[0.2em] mb-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            O que fazemos
          </motion.p>
          <div className="flex items-end gap-6">
            <motion.h2
              className="text-4xl sm:text-5xl font-bold text-[#062B63] font-outfit tracking-tight"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.6 }}
            >
              Nossos serviços
            </motion.h2>
            <motion.div
              className="hidden sm:block h-px flex-1 bg-[#062B63]/10 mb-3"
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              style={{ transformOrigin: 'left' }}
              transition={{ delay: 0.3, duration: 0.8 }}
            />
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {services.map((service, i) => (
            <ServiceCard key={service.id} service={service} index={i} />
          ))}
        </div>
      </div>
    </section>
  )
}

export default ServicesSection
